import React, { useEffect, useState } from 'react';
import {
    DateRow,
    PriceRow,
    TextRow,
    UnitsRow,
    SimpleTooltip,
    DisplayIdRow,
    LinkRow,
    ApptDetailRow,
    renderCptCodeAndModifiers,
    TypeRow,
    StatusCard,
    EditWithIcon,
    ToolInfo,
    CustomDelete,
} from 'components';
import {
    evalModeEnums,
    FindLoad,
    FindSuccessItem,
    formatAMPM,
    hooksForTable,
    Images,
    manageStatus,
    manageStatusCircle,
    PermissionList,
    RolePermission,
    useModal,
} from 'utils';
import moment from 'moment/moment';
import { Show1500Form } from '../../../../pages/billing/claims/show1500Form';
import { FormType1500 } from '../../../../pages/billing/claims';
import { ApptStateInfo } from '../../../schedule/core/modals/apptDetail/apptStateInfo';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox } from '@mui/material';
import { secondaryStatusEnums } from '../../../../pages/billing/claimPayment/constants';
import { claimActions, httpRequestsOnSuccessActions } from '../../../../store';
import { EDIT_READY_SECONDARY_BILLING, REFRESH_APPT_UNITS } from '../../../../store/billing/claim/claim.type';
import { ClaimAdjustmentCell } from '../../../../pages/billing/claim/core';

export const claimDetailTabsLabels = [
    { label: 'Details' },
    { label: 'Appointments' },
    { label: 'Comments' },
    RolePermission([PermissionList.CLAIM_NOTE_MANAGE?.code]) && { label: 'Notes' },
    RolePermission([PermissionList.CLAIM_HISTORY_READ?.code]) && { label: 'History' },
    RolePermission([PermissionList.CLAIM_FILE_READ?.code]) && { label: 'Files' },
];

export function getClaimDetails(claim) {
    const ACTION_TYPE = 'EDIT_READY_SECONDARY_BILLING';
    const enrolments = useSelector((state) => state.client.clientEnrollment);
    const [secondaryInfo, setSecondaryInfo] = useState({});
    const dispatch = useDispatch();
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const checkReady = claim?.secondaryBilling?.secondaryBillingState === secondaryStatusEnums.READY;

    useEffect(() => {
        if (success) {
            const params = {
                ...secondaryInfo,
            };
            params.secondaryPayerSelect = false;
            params.authIdField = false;
            setSecondaryInfo(params);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    const { open } = useModal();
    const { staffFullName, staffId, clientFullName, clientId, funderName, funderId, dos, createdDate, submittedDate, paidAmount, status } =
        claim || {};
    const { handleCreatedAtDate } = hooksForTable;

    return [
        {
            detailText: 'Staff:',
            detail: (
                <LinkRow
                    name={staffFullName ? staffFullName : '--'}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${staffId}` : ''}
                    checkPermission={staffId ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                />
            ),
        },
        {
            detailText: 'DOS:',
            detail: `${handleCreatedAtDate(dos?.early)} - ${handleCreatedAtDate(dos?.latest)}`,
        },
        {
            detailText: 'Total Units:',
            detail: claim?.totalUnits ? claim?.totalUnits?.toFixed(2) : '--',
        },
        {
            detailText: 'Client:',
            detail: (
                <LinkRow
                    name={clientFullName ? clientFullName : '--'}
                    href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${clientId}` : ''}
                    checkPermission={clientId ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                />
            ),
        },
        {
            detailText: 'Created Date:',
            detail: createdDate ? <DateRow date={createdDate} /> : '--',
        },
        {
            detailText: 'Billed Amount:',
            detail: <PriceRow info={claim?.billedAmount} />,
        },
        {
            detailText: 'Payer:',
            detail: (
                <div className="flex-align-center full-width" style={{ gap: 8 }}>
                    {secondaryInfo?.secondaryPayerSelect ? (
                        <div className="flex-align-center full-width" style={{ gap: 16 }}>
                            <select
                                className="auth-id-input"
                                name="evalMode"
                                value={secondaryInfo?.secondaryPayer || ''}
                                onChange={(e) => {
                                    const params = {
                                        ...secondaryInfo,
                                        secondaryPayer: e.target.value,
                                        secondaryPayerName: enrolments.find((enrol) => enrol.id === e.target.value)?.payerName || '',
                                    };
                                    setSecondaryInfo(params);
                                }}
                            >
                                <option value="" disabled hidden>
                                    Select Secondary Payer
                                </option>
                                {enrolments?.length ? (
                                    enrolments.map((i, j) => (
                                        <option key={j} value={i?.payerId}>
                                            {`${i?.payerName} - ${i?.enrollmentId} (${i?.terminationDate ? 'Inactive' : 'Active'})`}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>N/A</option>
                                )}
                            </select>

                            <div className="flex-align-center" style={{ gap: 8 }}>
                                <EditWithIcon
                                    load={loader?.length}
                                    id={'secondary-billing'}
                                    icon={Images.checkmark}
                                    type={'button'}
                                    handleClick={() => {
                                        if (secondaryInfo?.secondaryPayer) {
                                            dispatch(
                                                claimActions.editReadySecondaryBilling(claim?.id, {
                                                    payerId: secondaryInfo?.secondaryPayer,
                                                    // authId: claim?.authorizationId,
                                                    // secondaryBillingState: secondaryStatusEnums.READY,
                                                })
                                            );
                                        }
                                    }}
                                />
                                <EditWithIcon
                                    id={'secondary-billing'}
                                    icon={Images.remove}
                                    type={'button'}
                                    handleClick={() => {
                                        const params = {
                                            ...secondaryInfo,
                                        };
                                        delete params.secondaryPayer;
                                        delete params.secondaryPayerName;
                                        params.secondaryPayerSelect = false;
                                        setSecondaryInfo(params);
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-align-center full-width" style={{ gap: 8 }}>
                            <div className="full-width">
                                {checkReady && claim?.secondaryBilling?.payerId && claim?.secondaryBilling?.payerName ? (
                                    <LinkRow
                                        name={claim?.secondaryBilling?.payerName ? claim?.secondaryBilling?.payerName : '--'}
                                        href={
                                            RolePermission([PermissionList.FS_READ.code])
                                                ? `/fundingSource/${claim?.secondaryBilling?.payerId}`
                                                : ''
                                        }
                                        checkPermission={
                                            claim?.secondaryBilling?.payerId ? RolePermission([PermissionList.FS_READ.code]) : null
                                        }
                                    />
                                ) : (
                                    <LinkRow
                                        name={funderName ? funderName : '--'}
                                        href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${funderId}` : ''}
                                        checkPermission={funderId ? RolePermission([PermissionList.FS_READ.code]) : null}
                                    />
                                )}
                            </div>
                            {checkReady && claim?.funderId !== claim?.secondaryBilling?.payerId && (
                                <div className="secondary-payer-badge">Secondary</div>
                            )}
                            {checkReady && (
                                <button
                                    onClick={() => {
                                        const params = {
                                            ...secondaryInfo,
                                        };
                                        params.secondaryPayerSelect = true;
                                        setSecondaryInfo(params);
                                    }}
                                    className="reset-btn-by-default"
                                >
                                    <img src={Images.edit} alt="edit" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ),
        },
        {
            detailText: 'Submitted Date:',
            detail: submittedDate ? <DateRow date={submittedDate} /> : '--',
        },
        {
            detailText: 'Allowed Amount:',
            detail: <PriceRow info={claim?.allowedAmount} />,
        },
        {
            detailText: 'Auth ID:',
            detail: (
                <div>
                    {secondaryInfo?.authIdField ? (
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            <input
                                type="text"
                                className="auth-id-input"
                                placeholder={'Enter Auth ID'}
                                name={'authId'}
                                value={secondaryInfo?.authId ? secondaryInfo?.authId : ''}
                                onChange={(e) => {
                                    const params = {
                                        ...secondaryInfo,
                                    };
                                    params.authId = e.target.value;
                                    setSecondaryInfo(params);
                                }}
                            />

                            <div className="flex-align-center" style={{ gap: 8 }}>
                                <EditWithIcon
                                    load={loader?.length}
                                    id={'secondary-billing'}
                                    icon={Images.checkmark}
                                    type={'button'}
                                    handleClick={() => {
                                        if (secondaryInfo?.authId) {
                                            dispatch(
                                                claimActions.editReadySecondaryBilling(claim?.id, {
                                                    // payerId: secondaryInfo?.secondaryPayer ? secondaryInfo?.secondaryPayer : claim?.funderId,
                                                    // payerName: secondaryInfo?.secondaryPayerName ? secondaryInfo?.secondaryPayerName : claim?.funderName,
                                                    authId: secondaryInfo?.authId,
                                                    // secondaryBillingState: secondaryStatusEnums.READY,
                                                })
                                            );
                                        }
                                    }}
                                />
                                <EditWithIcon
                                    id={'secondary-billing'}
                                    icon={Images.remove}
                                    type={'button'}
                                    handleClick={() => {
                                        const params = {
                                            ...secondaryInfo,
                                        };
                                        delete params.authId;
                                        params.authIdField = false;
                                        setSecondaryInfo(params);
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            {checkReady && claim?.secondaryBilling?.authID ? (
                                <p className="full-width">{claim?.secondaryBilling?.authID}</p>
                            ) : (
                                <p className="full-width">{claim?.authorizationId}</p>
                            )}
                            {checkReady && (
                                <button
                                    onClick={() => {
                                        const params = {
                                            ...secondaryInfo,
                                        };
                                        params.authIdField = true;
                                        setSecondaryInfo(params);
                                    }}
                                    className="reset-btn-by-default"
                                >
                                    <img src={Images.edit} alt="edit" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ),
        },
        {
            detailText: '1500 Form:',
            detail: (
                <div className="flex-align-center" style={{ gap: 12 }}>
                    <button onClick={() => open(<FormType1500 item={claim} />)} className="download-1500">
                        <p>Download</p>
                        <img src={Images.downloadCloudBlue} alt="" />
                    </button>
                    <Show1500Form item={claim} />
                </div>
            ),
        },
        {
            detailText: 'Paid Amount:',
            detail: <PriceRow info={paidAmount} />,
        },
        {
            detailText: 'Member ID:',
            detail: claim?.clientMemberId ? claim?.clientMemberId : '--',
        },
        {
            detailText: 'Status:',
            detail: (
                <div className="flex-align-center">
                    <div>{manageStatusCircle(status)}</div>
                    {/*<div>{manageStatus(status)}</div>*/}
                    <TypeRow text={status ? status : '--'} />

                    {status === APPEAL && (
                        <div
                            className="flex-align-center full-width"
                            style={{ marginLeft: 6, paddingLeft: '6px', borderLeft: '1px solid #667085' }}
                        >
                            <p style={{ color: '#172B4D', fontWeight: 600, marginRight: 8 }}>Reason:</p>
                            <TextRow name={claim?.appealDetails ? claim?.appealDetails : '--'} />
                        </div>
                    )}
                </div>
            ),
        },
        {
            detailText: 'Balance:',
            detail: <PriceRow info={claim?.remaining} />,
        },
        {
            detailText: 'Additional Claim Info:',
            detail: claim?.additionalClaimInfo,
            background: '#FCEBE7',
        },
        {
            detailText: 'Resubmission Code:',
            detail: claim?.resubmissionCode,
            background: '#FCEBE7',
        },
        {
            detailText: 'Original Ref. No:',
            detail: claim?.originalRefNumber,
            background: '#FCEBE7',
        },

        claim?.company?.name && {
            detailText: 'Service Facility:',
            detail: claim?.company?.name,
        },
    ];
}

export const claimDetailHead = () => [
    { name: '', title: 'ID', noSearch: true, custom: false },
    { name: '', title: 'DOS', noSearch: true, custom: false, width: '110px' },
    { name: '', title: 'CPT Code', noSearch: true, custom: false },
    { name: '', title: 'Units', noSearch: true, custom: false },
    { name: '', title: 'Billed', noSearch: true, custom: false },
    { name: '', title: 'Allowed', noSearch: true, custom: false },
    { name: '', title: 'Client Total', noSearch: true, custom: false },
    { name: '', title: 'Paid', noSearch: true, custom: false },
    { name: '', title: 'Adjustments', noSearch: true, custom: false },
    { name: '', title: 'Balance', noSearch: true, custom: false },
];

export const claimDetailBody = (claim, afterSuccess) => [
    {
        rowText: (item) => (
            // claim?.status === PENDING_CLAIM ?
            // <ApptFlags
            //     id={item?.displayId ? item?.displayId : '--'}
            //     incomplete={item?.incompleteUnitFlag}
            //     removed={item?.removedFromClaimFlag}
            //     updateFlag={item?.updateFlag}
            // />
            // :
            <DisplayIdRow id={item?.displayId ? item?.displayId : '--'} />
        ),
    },
    {
        rowText: (item) => (
            <div className="flex-align-center">
                <DateRow date={item?.dos} />
            </div>
        ),
    },
    {
        rowText: (item) => <p>{renderCptCodeAndModifiers(item?.cptCode, item?.chargeRateModifier, item?.chargeRateModifier2, item?.chargeRateModifier3, item?.posModifier)}</p>,
    },
    {
        rowText: (item) => <UnitsRow info={item?.totalUnits} />,
    },
    {
        rowText: (item) => <PriceRow info={item?.totalBilled} />,
    },
    {
        rowText: (item) => <PriceRow info={item?.allowedAMT} />,
    },
    {
        rowText: (item) => <PriceRow info={item?.clientTotal} />,
    },
    {
        rowText: (item) => <PriceRow info={item?.payorPaid} />,
    },
    {
        rowText: (item) => <ClaimAdjustmentCell claim={claim} item={item} afterSuccess={() => afterSuccess('noLoad')} />,
        notClickable: true,
    },
    {
        rowText: (item) => <PriceRow info={item?.payorBalance} />,
    },
];

export const PENDING_CLAIM = 'PENDING';
export const CLOSED_CLAIM = 'CLOSED';
export const SUBMITTED_CLAIM = 'SUBMITTED';
export const COMPLETED = 'COMPLETED';
export const OPEN = 'OPEN';
export const APPEAL = 'APPEAL';
export const PAID = 'PAID';

export const claimAppointmentsHead = (handleSelect, fullList, selected) => {
    return [
        RolePermission([PermissionList.REMOVE_CLAIM_APPT?.code]) && {
            renderView: (
                <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                    <Checkbox
                        onClick={(e) => handleSelect(e, '', 'all')}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#0C66E4' }}
                        checked={fullList?.length === selected?.length}
                    />
                </SimpleTooltip>
            ),
            width: '30px',
        },
        { name: '', title: 'ID', noSearch: true, custom: false },
        { name: '', title: 'Date/Time', noSearch: true, custom: false },
        { name: '', title: 'Staff Member', noSearch: true, custom: false },
        { name: '', title: 'Client', noSearch: true, custom: false },
        { name: '', title: 'CPT Code (Mod.)', noSearch: true, custom: false },
        { name: '', title: 'Place of Service', noSearch: true, custom: false },
        { name: '', title: 'Units', noSearch: true, custom: false },
        // RolePermission([PermissionList.REMOVE_CLAIM_APPT?.code]) &&
        { name: ' ', title: ' ', noSearch: true, custom: false },
    ];
};

export const claimAppointmentsBody = (claimById, getClaimById, handleSelect, fullList, selected) => {
    return [
        RolePermission([PermissionList.REMOVE_CLAIM_APPT?.code]) && {
            button: (handle, item) => {
                return (
                        <Checkbox
                            disabled={item?.removedFromClaimFlag}
                            onClick={(e) => handleSelect(e, item)}
                            name={'checkBox'}
                            style={{ padding: 0, color: '#0C66E4', opacity: item?.removedFromClaimFlag ? 0.5 : 1 }}
                            checked={selected?.includes(item?.id)}
                        />
                );
            },
            notClickable: true,
        },

        {
            rowText: (item) => (
                <div className="flex-align-center" style={{ gap: 8 }}>
                    <ApptDetailRow
                        name={item?.displayId ? item.displayId : '--'}
                        id={item?.id}
                        afterSuccess={getClaimById}
                        showWidth={'auto'}
                    />
                    {!!item?.issues?.length && <ApptStateInfo appt={item} />}
                    {/*{claimById?.status === 'PENDING' &&*/}
                    {/*    <ApptFlags*/}
                    {/*        incomplete={item?.incompleteUnitFlag}*/}
                    {/*        removed={item?.removedFromClaimFlag}*/}
                    {/*        updateFlag={item?.updateFlag}*/}
                    {/*        singleAppt={true}*/}
                    {/*    />*/}
                    {/*}*/}
                </div>
            ),
        },
        {
            rowText: (item) => (
                <div>
                    <p>{moment.utc(item?.startTime).format('MM/DD/YYYY')}</p>
                    <p className="schedule-time">{`${formatAMPM(item?.startTime)} - ${formatAMPM(item?.endTime)}`}</p>
                </div>
            ),
        },
        {
            rowText: (item) => (
                <LinkRow
                    name={item?.staffFullName ? item.staffFullName : '--'}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.staffId}` : ''}
                    checkPermission={item?.staffId ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                />
            ),
        },
        {
            rowText: (item) => (
                <LinkRow
                    name={item?.clientFullName ? item.clientFullName : '--'}
                    href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.clientId}` : ''}
                    checkPermission={item?.clientId ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                />
            ),
        },
        { rowText: (item) => <TextRow name={item?.cptCode} /> },
        { rowText: (item) => <TextRow name={item?.placeOfService} /> },
        {
            button: (fn, item) => (
                <div className="flex-align-center" style={{ gap: 4 }}>
                    <UnitsRow info={item?.units} />
                    {!item?.removedFromClaimFlag && (
                        <SimpleTooltip title={<p>Refresh Units</p>} placement="top">
                            <button
                                className="reset-btn-by-default"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    fn('refresh');
                                }}
                            >
                                <img src={Images.refresh} alt="refresh" style={{ width: 16, height: 16 }} />
                            </button>
                        </SimpleTooltip>
                    )}
                </div>
            ),
        },
        {
            rowText: (item) =>
                item?.status !== 'PENDING' && (
                    <div>
                        {item?.removedFromClaimFlag && (
                            <SimpleTooltip title={<p>{'Appointment was removed from claim'}</p>} placement="bottom" arrow>
                                <p style={{ color: '#E90715' }}>Removed</p>
                            </SimpleTooltip>
                        )}
                    </div>
                ),
        },
        // RolePermission([PermissionList.REMOVE_CLAIM_APPT?.code]) &&
        // {
        //     button: (fn, item) => item?.status !== 'PENDING' && (
        //         <div>
        //             {item?.removedFromClaimFlag ?
        //                 <SimpleTooltip
        //                     title={<p>{'Appointment was removed from claim'}</p>}
        //                     placement="bottom"
        //                     arrow
        //                 >
        //                     <p style={{ color: '#E90715' }}>Removed</p>
        //                 </SimpleTooltip>
        //                 :
        //                 <button className="remove-from-claim" onClick={fn}>Remove from Claim</button>
        //             }
        //         </div>
        //     ),
        // },
    ];
};
